import { v4 as uuidv4 } from 'uuid';
import { Product, SalesPackage } from '../types';

const PRODUCT_CATEGORY = 'Subscription/Broadband/Fixed wireless';
const WIFIX_PRODUCT_CATEGORY = 'Hardware/Router/Extender';
const wifix_cpid = 1681;
const BRAND = 'Telia';

// Checkout = what you do after add to cart and before transaction
// We could use the CHECKOUT event to track how many customers who come to the personalia page,
// but it is kind of redundant since the second addToCart means the same thing.
enum GTMEvent {
  IMPRESSION = 'impression', // show product
  PRODUCT_CLICK = 'productClick',
  ADD_TO_CART = 'addToCart', // add product to shopping cart
  DELIVERY = 'DELIVERY',
  TRANSACTION = 'transaction' // buy all the products in shopping cart
}
interface Dimension {
  [key: string]: string;
}
const getListofProductNames = (products: (SalesPackage | Product)[]) => {
  return products.map(product => product.name).toString();
};

const getDimensionsFor = (eventType: GTMEvent, products: (SalesPackage | Product)[]): Dimension => {
  if (eventType === GTMEvent.IMPRESSION) {
    return {};
  } else if (eventType === GTMEvent.ADD_TO_CART) {
    return {
      dimension1: 'Billing',
      dimension2: 'Subscription/Broadband/Fixed wireless|Service/Delivery',
      dimension3: 'Acquisition'
    };
  } else if (eventType === GTMEvent.DELIVERY) {
    return {
      dimension2: 'Subscription/Broadband/Fixed wireless|Service/Delivery',
      dimension74: getListofProductNames(products),
      dimension80: 'Families'
    };
  } else {
    return {
      dimension1: 'Billing',
      dimension75: 'Levert på døren',
      dimension2: 'Subscription/Broadband/Fixed wireless|Service/Delivery',
      dimension74: getListofProductNames(products),
      dimension80: 'Families'
    };
  }
};

export const pushPageview = (page: string) => {
  // @ts-ignore
  (window.dataLayer || []).push({
    event: 'vpageview',
    page: '/virtual/fixed_wireless' + page
  });
};

const productWithDimensions = (dimensions: Dimension) => (product: SalesPackage | Product, idx: number) => {
  const id = 'id' in product ? product.id : product.commercialProductId;
  const isWIFIX = id === wifix_cpid;

  return {
    brand: BRAND,
    category: isWIFIX ? WIFIX_PRODUCT_CATEGORY : PRODUCT_CATEGORY,
    id: id,
    name: product.name,
    price: product.adjustedRecurringCharge * 12 + product.adjustedEstablishmentFee,
    variant: product.name,
    list: PRODUCT_CATEGORY,
    position: idx,
    quantity: 1,
    ...dimensions
  };
};

export const pushProductClick = (salesPackage: SalesPackage | Product, position: number) => {
  // @ts-ignore
  (window.dataLayer || []).push({
    event: GTMEvent.PRODUCT_CLICK,
    ecommerce: {
      click: {
        actionField: { list: PRODUCT_CATEGORY },
        products: [
          {
            brand: BRAND,
            category: PRODUCT_CATEGORY,
            id: 'id' in salesPackage ? salesPackage.id : salesPackage.commercialProductId,
            name: salesPackage.name,
            price: salesPackage.adjustedRecurringCharge * 12 + salesPackage.adjustedEstablishmentFee,
            variant: salesPackage.name,
            position: position,
            quantity: 1
          }
        ]
      }
    }
  });
};

export const pushPackageFound = (packages: string) => {
  // @ts-ignore
  (window.dataLayer || []).push({
    event: 'GAEvent',
    eventCategory: 'AddressSearch',
    eventAction: 'Adresse OK',
    eventLabel: packages
  });
};

export const pushNoPackageFound = () => {
  // @ts-ignore
  (window.dataLayer || []).push({
    event: 'GAEvent',
    eventCategory: 'AddressSearch',
    eventAction: 'Avslag',
    eventLabel: 'ingen pakker tilgjengelig på adresse'
  });
};

export const pushProductImpression = (packages: (SalesPackage | Product)[]) => {
  const products = productWithDimensions({});
  const productsImpressions = packages.map(products);

  // @ts-ignore
  (window.dataLayer || []).push({
    event: GTMEvent.IMPRESSION,
    ecommerce: {
      currencyCode: 'NOK',
      impressions: productsImpressions
    }
  });
};

export const pushAddToCart = (packages: (SalesPackage | Product)[]) => {
  const dimensions = getDimensionsFor(GTMEvent.ADD_TO_CART, packages);
  const products = productWithDimensions(dimensions);
  const productsToCart = packages.map(products);

  // @ts-ignore
  (window.dataLayer || []).push({
    event: GTMEvent.ADD_TO_CART,
    ecommerce: {
      currencyCode: 'NOK',
      add: {
        products: productsToCart
      }
    }
  });
};

const firstYearPriceReducer = (accumulated: number, currentProduct: SalesPackage | Product) =>
  accumulated + currentProduct.adjustedRecurringCharge * 12 + currentProduct.adjustedEstablishmentFee;

// TODO: Add "tilknytningskostnad" 699,- for Trådløst bredbånd small/medium
// TODO: We should track attempted transactions (now we only track success)
export const pushTransaction = (salesPackages: SalesPackage[], additionalProduct: Product[]) => {
  const shippingPrice = 129;
  const extraProduct = {
    name: 'Levert på døren',
    id: GTMEvent.DELIVERY,
    price: shippingPrice,
    brand: BRAND,
    category: 'Service/Delivery',
    quantity: 1,
    ...getDimensionsFor(GTMEvent.DELIVERY, salesPackages)
  };

  const transactionId = `fwa-${uuidv4()}`;

  const totalPriceMain = salesPackages.reduce(firstYearPriceReducer, 0);
  const totalPriceAddon = additionalProduct.reduce(firstYearPriceReducer, 0);
  const totalPrice = totalPriceMain + totalPriceAddon + shippingPrice;
  const totalVAT = totalPrice / 5;

  const dimensions = getDimensionsFor(GTMEvent.TRANSACTION, salesPackages);
  const products = productWithDimensions(dimensions);
  const productsTransaction = [...salesPackages, ...additionalProduct].map(products);

  // @ts-ignore
  (window.dataLayer || []).push({
    event: GTMEvent.TRANSACTION,
    ecommerce: {
      purchase: {
        actionField: {
          id: transactionId, // Transaction ID. Required for purchases and refunds.
          affiliation: 'Telia Min Side',
          revenue: totalPrice, // Total transaction value (incl. tax and shipping)
          tax: totalVAT
        },
        products: [...productsTransaction, extraProduct]
      }
    }
  });
};
